import { Tile, TileType, TraySlot, GameState, Layout, Theme, Sprites, TRAY_SIZE, ATLAS_COLS } from './types';
import { TILES } from './tiles';
import { isOpen } from './game';
import { getLevelDims } from './generator';
import atlasUrl from '../assets/atlas.png';

// ── Helpers ───────────────────────────────────────────────────────────────

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  ctx.lineTo(x + rr, y + h);
  ctx.arcTo(x, y + h, x, y + h - rr, rr);
  ctx.lineTo(x, y + rr);
  ctx.arcTo(x, y, x + rr, y, rr);
  ctx.closePath();
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

// ── In-game menu (☰ → overlay: Продолжить / Заново / В хаб) ─────────────────
// Manual roundRect (arcTo) — no native ctx.roundRect, per project convention.
function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}

export type MenuRect = { x: number; y: number; w: number; h: number };
export type MenuCircle = { cx: number; cy: number; r: number };

const MENU_TOGGLE = 40; // ☰-button diameter
const MENU_EDGE = 14;   // inset from screen edge

// Single geometry source for both drawMenu (draw) and the tap handler (hit-test).
export function menuRects(viewW: number, viewH: number) {
  const toggle: MenuCircle = {
    cx: MENU_EDGE + MENU_TOGGLE / 2,
    cy: MENU_EDGE + MENU_TOGGLE / 2,
    r: MENU_TOGGLE / 2,
  };
  const bw = Math.min(260, viewW - 64);
  const bh = 52, gap = 14;
  const px = (viewW - bw) / 2;
  const total = bh * 3 + gap * 2;
  const py = (viewH - total) / 2;
  const resume:  MenuRect = { x: px, y: py,                    w: bw, h: bh };
  const restart: MenuRect = { x: px, y: py + (bh + gap),       w: bw, h: bh };
  const hub:     MenuRect = { x: px, y: py + (bh + gap) * 2,   w: bw, h: bh };
  const panel:   MenuRect = { x: px - 20, y: py - 24, w: bw + 40, h: total + 48 };
  return { toggle, panel, resume, restart, hub };
}

export function inMenuRect(x: number, y: number, r: MenuRect): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

export function drawMenu(
  ctx: CanvasRenderingContext2D,
  viewW: number, viewH: number,
  phase: GameState['phase'],
  menuOpen: boolean,
  theme: Theme,
): void {
  if (phase !== 'playing') return; // ☰ только во время игры
  const { toggle, panel, resume, restart, hub } = menuRects(viewW, viewH);

  drawButton(ctx, toggle.cx, toggle.cy, toggle.r, '☰', theme, 1);

  if (!menuOpen) return;

  ctx.save();
  ctx.fillStyle = theme.overlayBg;
  ctx.fillRect(0, 0, viewW, viewH);

  roundRectPath(ctx, panel.x, panel.y, panel.w, panel.h, 18);
  ctx.fillStyle = theme.trayBg;
  ctx.fill();
  ctx.strokeStyle = theme.trayStroke;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  const items: [MenuRect, string, boolean][] = [
    [resume,  'Продолжить', false],
    [restart, 'Заново',     false],
    [hub,     'В хаб',      true], // акцентный
  ];
  for (const [r, label, accent] of items) {
    roundRectPath(ctx, r.x, r.y, r.w, r.h, 12);
    ctx.fillStyle = accent ? theme.tileOpenGlow : theme.btnBg;
    ctx.fill();
    ctx.strokeStyle = theme.btnStroke;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = accent ? '#fff' : theme.overlayText;
    ctx.font = 'bold 17px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
  }
  ctx.restore();
}

// ── Sprite loading ─────────────────────────────────────────────────────────

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export async function loadSprites(): Promise<Sprites> {
  // Атлас лежал в public/ и грузился относительным путём; в Rails такой путь
  // указывает мимо, поэтому URL берётся у Vite — он же добавляет хеш.
  const atlas = await loadImage(atlasUrl);
  return { atlas };
}

// ── Theme ─────────────────────────────────────────────────────────────────

export function readTheme(): Theme {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();
  return {
    bg:           v('--bg'),
    fieldBg:      v('--field-bg'),
    tileOpenGlow: v('--tile-open-glow'),
    tileDim:      v('--tile-dim'),
    trayBg:       v('--tray-bg'),
    trayStroke:   v('--tray-stroke'),
    traySlotBg:   v('--tray-slot-bg'),
    trayDanger:   v('--tray-danger'),
    textUi:       v('--text-ui'),
    textDim:      v('--text-dim'),
    btnBg:        v('--btn-bg'),
    btnStroke:    v('--btn-stroke'),
    overlayBg:    v('--overlay-bg'),
    overlayText:  v('--overlay-text'),
    overlaySub:   v('--overlay-sub'),
  };
}

// ── Layout ────────────────────────────────────────────────────────────────

export function computeLayout(W: number, H: number, levelIdx: number): Layout {
  const dims = getLevelDims(levelIdx);
  const BTN_R = Math.min(24, W * 0.06);
  const TRAY_GAP = 5;
  const TRAY_V_PAD = 10;
  const TOP_PAD = 40;
  const BOTTOM_MARGIN = 10;

  // Tray: centre 7 slots, leave room for buttons on each side
  const trayInnerW = W - 4 * (BTN_R + 6);
  const rawSlot = (trayInnerW - 6 * TRAY_GAP) / TRAY_SIZE;
  const traySlotSize = Math.min(rawSlot, 54);
  const trayH = traySlotSize + 2 * TRAY_V_PAD;
  const trayY = H - trayH - BOTTOM_MARGIN;
  const trayTotalW = TRAY_SIZE * traySlotSize + 6 * TRAY_GAP;
  const trayStartX = (W - trayTotalW) / 2;

  // Buttons: vertically centred in tray band
  const btnY = trayY + trayH / 2;
  const undoBtnX = trayStartX - BTN_R - 8;
  const hintBtnX = trayStartX + trayTotalW + BTN_R + 8;

  // Field: remaining area above tray
  const fieldAreaH = trayY - TOP_PAD - 6;
  const fieldAreaW = W - 16;

  // Grid pixel footprint: tile at colX2=max has right edge at (max/2 + 1) * cellSize
  const fieldCols = dims.maxColX2 / 2 + 1;
  const fieldRows = dims.maxRowX2 / 2 + 1;

  const cellByW = fieldAreaW / fieldCols;
  const cellByH = fieldAreaH / fieldRows;
  const cellSize = Math.min(cellByW, cellByH, 96);
  const halfCell = cellSize / 2;

  const totalFieldW = fieldCols * cellSize;
  const totalFieldH = fieldRows * cellSize;
  const fieldX = (W - totalFieldW) / 2;
  const fieldY = TOP_PAD + (fieldAreaH - totalFieldH) / 2;

  return {
    W, H,
    cellSize, halfCell,
    fieldX, fieldY,
    trayY, trayH,
    traySlotSize,
    trayStartX,
    trayGap: TRAY_GAP,
    undoBtnX, undoBtnY: btnY,
    hintBtnX, hintBtnY: btnY,
    btnR: BTN_R,
  };
}

// ── Tile pixel helpers ────────────────────────────────────────────────────

// Higher layers shift up-left slightly to create visible depth (3D stacking look).
const LAYER_OFFSET_PX = 3;

export function tilePixelPos(tile: Tile, layout: Layout): { x: number; y: number } {
  return {
    x: layout.fieldX + tile.col * layout.halfCell * 2 - tile.layer * LAYER_OFFSET_PX,
    y: layout.fieldY + tile.row * layout.halfCell * 2 - tile.layer * LAYER_OFFSET_PX,
  };
}

export function traySlotPos(slotIdx: number, layout: Layout): { x: number; y: number } {
  const x = layout.trayStartX + slotIdx * (layout.traySlotSize + layout.trayGap);
  const y = layout.trayY + layout.trayH / 2 - layout.traySlotSize / 2;
  return { x, y };
}

// ── Hit testing ───────────────────────────────────────────────────────────

export function tileAtPoint(
  tapX: number, tapY: number,
  state: GameState,
  layout: Layout,
): Tile | null {
  const cs = layout.cellSize;
  const candidates: Tile[] = [];

  for (const tile of state.fieldTiles) {
    const { x, y } = tilePixelPos(tile, layout);
    if (tapX >= x && tapX <= x + cs && tapY >= y && tapY <= y + cs) {
      candidates.push(tile);
    }
  }

  // Highest layer first, then first open tile wins
  candidates.sort((a, b) => b.layer - a.layer);
  return candidates.find(t => isOpen(t, state.fieldTiles)) ?? null;
}

export function hitCircle(
  x: number, y: number, cx: number, cy: number, r: number,
): boolean {
  return (x - cx) ** 2 + (y - cy) ** 2 <= r * r;
}

// ── Drawing tiles ─────────────────────────────────────────────────────────

function drawTileAt(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, size: number,
  type: TileType,
  sprites: Sprites,
  alpha = 1,
): void {
  ctx.save();
  ctx.globalAlpha = alpha;

  if (sprites.atlas) {
    const ATLAS_MARGIN = 35, ATLAS_GAP = 14;
    const tileW = (sprites.atlas.naturalWidth - 2 * ATLAS_MARGIN - 4 * ATLAS_GAP) / ATLAS_COLS;
    const sx = ATLAS_MARGIN + (type % ATLAS_COLS) * (tileW + ATLAS_GAP);
    const sy = ATLAS_MARGIN + Math.floor(type / ATLAS_COLS) * (tileW + ATLAS_GAP);
    ctx.drawImage(sprites.atlas, sx, sy, tileW, tileW, x, y, size, size);
  } else {
    const def = TILES[type];
    const r = size * 0.18;

    // Shadow
    ctx.shadowColor = 'rgba(100,70,160,0.25)';
    ctx.shadowBlur = size * 0.15;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = size * 0.06;

    // Background
    roundedRect(ctx, x, y, size, size, r);
    ctx.fillStyle = def.bg;
    ctx.fill();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Top-left highlight
    roundedRect(ctx, x + 1, y + 1, size - 2, size * 0.45, r);
    ctx.fillStyle = 'rgba(255,255,255,0.40)';
    ctx.fill();

    // Emoji
    ctx.font = `${Math.round(size * 0.54)}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#000';
    ctx.fillText(def.emoji, x + size / 2, y + size / 2 + size * 0.03);
  }

  ctx.restore();
}

// ── Field tiles cache ───────────────────────────────────────────────────────
// state.fieldTiles is only replaced (new array reference) when a tile leaves
// the field (game.ts is immutable), so caching keyed on reference identity
// avoids re-sorting and re-running O(n²) isOpen checks every RAF frame.

let lastFieldTiles: Tile[] | null = null;
let cachedSorted: Tile[] = [];
let cachedOpenSet: Set<number> = new Set();

function getFieldRenderCache(fieldTiles: Tile[]): { sorted: Tile[]; openSet: Set<number> } {
  if (fieldTiles !== lastFieldTiles) {
    lastFieldTiles = fieldTiles;
    cachedSorted = [...fieldTiles].sort(
      (a, b) => a.layer !== b.layer ? a.layer - b.layer : a.row - b.row,
    );
    cachedOpenSet = new Set(
      fieldTiles.filter(t => isOpen(t, fieldTiles)).map(t => t.id),
    );
  }
  return { sorted: cachedSorted, openSet: cachedOpenSet };
}

// ── Main render ───────────────────────────────────────────────────────────

export function renderFrame(
  ctx: CanvasRenderingContext2D,
  layout: Layout,
  state: GameState,
  theme: Theme,
  sprites: Sprites,
  nowMs: number,
): void {
  const { W, H, cellSize, fieldY } = layout;

  // Background
  ctx.fillStyle = theme.bg;
  ctx.fillRect(0, 0, W, H);

  // Level indicator
  ctx.fillStyle = theme.textDim;
  ctx.font = `${Math.round(layout.btnR * 0.7)}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`Level ${state.level + 1}`, W / 2, 20);

  // ── Field background ───────────────────────────────────────────────────
  {
    const dims = getLevelDims(state.level);
    const fieldW = (dims.maxColX2 / 2 + 1) * cellSize;
    const fieldH = (dims.maxRowX2 / 2 + 1) * cellSize;
    roundedRect(ctx, layout.fieldX - 8, fieldY - 8, fieldW + 16, fieldH + 16, 16);
    ctx.fillStyle = theme.fieldBg;
    ctx.fill();
  }

  // ── Field tiles ────────────────────────────────────────────────────────
  const flyingId = state.flyAnim?.tile.id ?? -1;

  // Sort: layer asc, then row asc (for correct visual overlap within same layer)
  const { sorted, openSet } = getFieldRenderCache(state.fieldTiles);

  for (const tile of sorted) {
    if (tile.id === flyingId) continue; // drawn separately as fly anim

    const { x, y } = tilePixelPos(tile, layout);
    const open = openSet.has(tile.id);
    const isHinted = tile.id === state.hintTileId &&
                     nowMs - state.hintStartMs < 2000;

    drawTileAt(ctx, x, y, cellSize, tile.type, sprites);

    if (!open) {
      // Strong dim overlay so blocked tiles are clearly darker but still visible
      ctx.save();
      roundedRect(ctx, x, y, cellSize, cellSize, cellSize * 0.18);
      ctx.fillStyle = theme.tileDim;
      ctx.fill();
      ctx.restore();
    } else {
      // Open tile: bright border — clearly interactive
      ctx.save();
      roundedRect(ctx, x + 1.5, y + 1.5, cellSize - 3, cellSize - 3, cellSize * 0.17);
      ctx.strokeStyle = theme.tileOpenGlow;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.restore();
    }

    // Hint pulse
    if (isHinted) {
      const pulse = 0.5 + 0.5 * Math.sin((nowMs - state.hintStartMs) / 150);
      ctx.save();
      roundedRect(ctx, x - 2, y - 2, cellSize + 4, cellSize + 4, cellSize * 0.20);
      ctx.strokeStyle = `rgba(255,220,0,${0.6 + 0.4 * pulse})`;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.restore();
    }
  }

  // ── Tray ──────────────────────────────────────────────────────────────
  const { trayY, trayH, trayStartX, traySlotSize, trayGap } = layout;
  const trayFilledCount = state.tray.filter(s => s !== null).length;
  const isDanger = trayFilledCount >= 6;

  // Tray background
  const trayTotalW = TRAY_SIZE * traySlotSize + 6 * trayGap;
  roundedRect(ctx, trayStartX - 8, trayY - 4, trayTotalW + 16, trayH + 8, 16);
  ctx.fillStyle = isDanger ? theme.trayDanger : theme.trayBg;
  ctx.fill();
  ctx.strokeStyle = theme.trayStroke;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Tray slots
  const trayDisplayTiles: TraySlot[] = state.tray;
  const flyingSlot = state.flyAnim?.toSlot ?? -1;

  for (let i = 0; i < TRAY_SIZE; i++) {
    const { x, y } = traySlotPos(i, layout);

    // Slot background
    roundedRect(ctx, x, y, traySlotSize, traySlotSize, traySlotSize * 0.18);
    ctx.fillStyle = theme.traySlotBg;
    ctx.fill();

    const slotTile = trayDisplayTiles[i];
    if (slotTile !== null && i !== flyingSlot) {
      // Match animation: scale + fade for matched slots
      if (state.matchAnim?.slots.includes(i)) {
        const t = Math.min(1, (nowMs - state.matchAnim.startMs) / state.matchAnim.durationMs);
        const scale = t < 0.4 ? 1 + t * 0.6 : 1 + (1 - t) * 0.24;
        const alpha = t < 0.5 ? 1 : 1 - (t - 0.5) * 2;
        const offset = traySlotSize * (scale - 1) / 2;
        ctx.save();
        ctx.globalAlpha = alpha;
        drawTileAt(ctx, x - offset, y - offset, traySlotSize * scale, slotTile.type, sprites);
        ctx.restore();
      } else {
        drawTileAt(ctx, x, y, traySlotSize, slotTile.type, sprites);
      }
    }
  }

  // ── Flying tile ────────────────────────────────────────────────────────
  if (state.flyAnim) {
    const { tile, fromX, fromY, toSlot, startMs, durationMs } = state.flyAnim;
    const t = easeOutCubic(Math.min(1, (nowMs - startMs) / durationMs));
    const { x: tx, y: ty } = traySlotPos(toSlot, layout);
    const cx = fromX + (tx - fromX) * t;
    const cy = fromY + (ty - fromY) * t;
    // Scale from field cellSize down to tray slot size
    const size = cellSize + (traySlotSize - cellSize) * t;
    drawTileAt(ctx, cx, cy, size, tile.type, sprites);
  }

  // ── Particles ─────────────────────────────────────────────────────────
  for (const p of state.particles) {
    const t = (nowMs - p.startMs) / p.durationMs;
    if (t >= 1) continue;
    const px = p.x + p.vx * t * p.durationMs / 1000;
    const py = p.y + p.vy * t * p.durationMs / 1000;
    ctx.save();
    ctx.globalAlpha = 1 - t;
    ctx.beginPath();
    ctx.arc(px, py, p.r * (1 - t * 0.5), 0, Math.PI * 2);
    ctx.fillStyle = p.color;
    ctx.fill();
    ctx.restore();
  }

  // ── Buttons ────────────────────────────────────────────────────────────
  if (state.phase === 'playing') {
    const hasUndo = state.undo !== null;
    drawButton(ctx, layout.undoBtnX, layout.undoBtnY, layout.btnR, '↩', theme, hasUndo ? 1 : 0.35);
    drawButton(ctx, layout.hintBtnX, layout.hintBtnY, layout.btnR, '💡', theme, 1);
  }

  // ── Overlays ───────────────────────────────────────────────────────────
  if (state.phase === 'won') {
    drawOverlay(ctx, W, H, '🎉', 'Уровень пройден!', 'Нажмите для продолжения', theme);
  } else if (state.phase === 'lost') {
    drawOverlay(ctx, W, H, '😔', 'Поднос переполнен', 'Нажмите для перезапуска', theme);
  }

  // Field tile count (debug-ish UX info)
  const remaining = state.fieldTiles.length;
  if (remaining > 0 && state.phase === 'playing') {
    ctx.fillStyle = theme.textDim;
    ctx.font = `${Math.round(layout.btnR * 0.6)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${remaining} tiles`, W / 2, fieldY - 12);
  }
}

function drawButton(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  icon: string,
  theme: Theme,
  alpha: number,
): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = theme.btnBg;
  ctx.fill();
  ctx.strokeStyle = theme.btnStroke;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.font = `${Math.round(r)}px serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(icon, cx, cy + 1);
  ctx.restore();
}

function drawOverlay(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  icon: string,
  title: string,
  sub: string,
  theme: Theme,
): void {
  ctx.fillStyle = theme.overlayBg;
  ctx.fillRect(0, 0, W, H);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.font = `${Math.round(H * 0.1)}px serif`;
  ctx.fillText(icon, W / 2, H * 0.36);

  ctx.font = `bold ${Math.round(H * 0.04)}px sans-serif`;
  ctx.fillStyle = theme.overlayText;
  ctx.fillText(title, W / 2, H * 0.5);

  ctx.font = `${Math.round(H * 0.025)}px sans-serif`;
  ctx.fillStyle = theme.overlaySub;
  ctx.fillText(sub, W / 2, H * 0.58);
}

export function spawnParticles(
  slots: number[],
  layout: Layout,
  type: TileType,
): import('./types').Particle[] {
  const color = TILES[type].bg;
  const particles: import('./types').Particle[] = [];
  const now = performance.now();

  for (const slotIdx of slots) {
    const { x, y } = traySlotPos(slotIdx, layout);
    const cx = x + layout.traySlotSize / 2;
    const cy = y + layout.traySlotSize / 2;

    for (let i = 0; i < 9; i++) {
      const angle = (i / 9) * Math.PI * 2 + Math.random() * 0.4;
      const speed = 60 + Math.random() * 80;
      particles.push({
        x: cx, y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        r: 4 + Math.random() * 4,
        color,
        startMs: now,
        durationMs: 500 + Math.random() * 200,
      });
    }
  }

  return particles;
}
